import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAccessToken } from '../../useAccessToken'
import { makeResultFetch } from '../../genericFetch'
import { apiRequestParams } from '../../fetchOptions'
import { useHandleRetry } from '../../useHandleRetry'
import { ScheduleRow } from '../../../interfaces/systemSettings/RevenueRecognitionSchedule'

const useFetch = () => {
  const getAccessToken = useAccessToken()

  const queryFunction = async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<ScheduleRow[]>(
        `//Settings/RevenueRecognitionExecutionDates`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        return data
      })

      return fetchedData
    })
  }

  return queryFunction
}

export const usePrefetchRevenueRecognitionSchedule = () => {
  const queryFunction = useFetch()
  const queryClient = useQueryClient()

  const prefetchQuery = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: ['revenueRecognitionSchedule'],
      queryFn: queryFunction,
    })
  }, [queryClient, queryFunction])

  return prefetchQuery
}

export const useRevenueRecognitionSchedule = () => {
  const queryFunction = useFetch()
  const handleRetry = useHandleRetry()

  const { isLoading, error, data } = useQuery({
    queryKey: ['revenueRecognitionSchedule'],
    queryFn: queryFunction,
    enabled: !!queryFunction,
    retry: handleRetry,
  })

  return { isLoading, error, data }
}
